import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { UserPlus } from 'lucide-react'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { laufFehlerText } from '@/lib/lauf-fehler'
import { KiArbeitet } from '@/components/ki/KiArbeitet'
import { datumSchreibweisen, type Fundstelle } from '@/components/original/fundstellen'
import { KiAuswahl, KiEinleitung, KiEntscheidungZelle, KiFuss, KiLesenKnopf, KiLiest, KiTabelle, KiVorschlag, KiZustand, kiFarbe, kiGelb, kiKnopf, kiTd, kiTh } from '@/components/ki-vorschlag/KiVorschlag'
import { useAuthStore } from '@/lib/auth.store'
import { applyReading, createReadingSupplier, discardReading, fetchReading, fetchReadingState, startReading } from '../api/incoming-invoices.api'
import { ART_TEXT, mengeText, tagText } from '../lib/texte'
import type { IncomingInvoiceDetail, Reading, ReadingField, ReadingLine, ReadingPairing, SupplierCandidate } from '../types/incoming-invoices.types'

/** Die Zeile, deren Stelle im Original gerade gezeigt wird */
const gewaehltKlasse = 'outline outline-2 -outline-offset-2 outline-violet-500/70'

/** Kennung einer Fundstelle: dieselbe an der Zeile des Vorschlags und an ihrer Markierung im Original. */
export const stelleKopf = (key: string) => `kopf:${key}`
export const stelleZeile = (index: number) => `zeile:${index}`

const FELD: Record<string, string> = {
  supplierId: 'Lieferant',
  kind: 'Art',
  supplierInvoiceNumber: 'Rechnungsnummer',
  invoiceDate: 'Rechnungsdatum',
  serviceDateFrom: 'Leistung von',
  serviceDateTo: 'Leistung bis',
  paymentTermsDays: 'Zahlungsziel (Tage)',
  dueDate: 'Fällig',
  discountPercent: 'Skonto %',
  discountDueDate: 'Skonto bis',
  currency: 'Währung',
  paymentIban: 'IBAN',
  paymentBic: 'BIC',
  paymentReference: 'Zahlungsreferenz',
  // Nachtrag 3, PT-7: kein Feld der Maske — übernommen sperrt es die Rechnung für den Zahlungslauf
  paymentBlockedReason: 'Zahlungssperre',
  statedNet: 'Netto laut Rechnung',
  statedTax: 'Steuer laut Rechnung',
  statedGross: 'Brutto laut Rechnung',
}
const TAGE = new Set(['invoiceDate', 'serviceDateFrom', 'serviceDateTo', 'dueDate', 'discountDueDate'])
const GELD = new Set(['statedNet', 'statedTax', 'statedGross'])
const FEHLER: Record<string, string> = {
  INCOMING_INVOICE_AI_CONSENT: 'Für die KI-Lesung fehlt die Einwilligung — sie steht in den Einstellungen der Eingangsrechnungen.',
  INCOMING_INVOICE_AI_OFF: 'Die KI ist für deinen Betrieb nicht eingeschaltet (Einstellungen → KI).',
  INCOMING_INVOICE_AI_FORMAT: 'Diese Datei kann die KI nicht lesen — nur PDF und Bilder.',
  INCOMING_INVOICE_AI_TOO_LARGE: 'Die Datei ist für die KI-Lesung zu groß (höchstens 25 MB).',
  INCOMING_INVOICE_AI_BUSY: 'Diese Rechnung wird gerade gelesen.',
  INCOMING_INVOICE_AI_NO_SUPPLIER: 'Die Lesung nennt keinen Aussteller, der sich anlegen ließe.',
}

const zeige = (key: string, wert: unknown, anzeige?: string | null): string => {
  if (wert === null || wert === undefined || wert === '') return ''
  if (key === 'supplierId') return anzeige ?? ''
  if (key === 'kind') return ART_TEXT[wert as keyof typeof ART_TEXT] ?? String(wert)
  if (TAGE.has(key)) return tagText(String(wert))
  if (GELD.has(key)) return geldText(Number(wert))
  return String(wert)
}

/** AP-44 5.1: womit gelesen wurde — nur, wenn eine Hausregel oder Lesehinweise mitgingen. */
const womit = (k: Reading['context']): string | null => {
  const teile = [k?.houseRule ? `Hausregeln (Fassung ${k.houseRule})` : null, k?.supplierHints ? `Lesehinweisen zu ${k.supplierHints.name}` : null].filter(Boolean)
  return teile.length ? `Gelesen mit ${teile.join(' und ')}.` : null
}

const zuordnungText = (z: ReadingPairing): string => {
  const n = z.confirmed + z.suggested
  // Nachtrag 3, PT-6: Ohne Nummerntreffer wurde über alle offenen Bestellungen des Lieferanten gesucht.
  const wo = z.orderNumber ? `Bestellung ${z.orderNumber}` : 'Offene Bestellungen des Lieferanten'
  const teile = [n === 0 ? `${wo}: Keine Position ließ sich von selbst zuordnen.` : `${wo}: ${n} ${n === 1 ? 'Position' : 'Positionen'} zugeordnet.`]
  if (z.suggested > 0) teile.push(`${z.suggested === n ? (n === 1 ? 'Sie ist' : 'Alle sind') : `${z.suggested} davon ${z.suggested === 1 ? 'ist' : 'sind'}`} ein Vorschlag — bitte im Abgleich prüfen und bestätigen.`)
  if (n === 0) teile.push('Im Abgleich lässt sich jede Position von Hand zuordnen.')
  return teile.join(' ')
}

/**
 * AP-34 Nachtrag 2, 5.3 — **der Vorschlag der KI**, eine eigene Schicht über der Maske. Sichere Felder
 * und Zeilen sind vorausgewählt, unsichere gelb mit ihrem Grund. Erst „Übernehmen" schreibt in die
 * Rechnung; was weder übernommen noch verworfen ist, sperrt das Abschließen.
 *
 * Hat die KI eine Bestellnummer gelesen, ordnet der Server die übernommenen Positionen dieser Bestellung
 * zu; was dabei herauskam, steht als Hinweis hier, die Auswahl für Uneindeutiges bei den Positionen.
 *
 * Rahmen, Zustände und Fußzeile kommen aus dem gemeinsamen Vorschlagsfenster
 * (`components/ki-vorschlag`, AP-22 Teil 6) — dasselbe Fenster wie beim Abgabezettel.
 */
export function Lesung({
  rechnung,
  onRechnung,
  onZeige,
  onFundstellen,
  gewaehlt,
  onFehler,
  onAbgleich,
}: {
  rechnung: IncomingInvoiceDetail
  onRechnung: (r: IncomingInvoiceDetail) => void
  /** Ein Klick auf eine Zeile: Das Original zeigt ihre Fundstelle, sonst ihre Seite (AP-39 Nachtrag 1). */
  onZeige: (stelle: string, seite: number | null) => void
  /** Was im Original zu markieren ist — solange der Vorschlag offen ist */
  onFundstellen?: (stellen: Fundstelle[]) => void
  /** Die Stelle, die zuletzt gezeigt wurde — auch nach einem Klick auf ihre Markierung im Original */
  gewaehlt?: string | null
  onFehler: (t: string | null) => void
  /** öffnet die Ansicht „Abgleich" — dort steht, was nach der Zuordnung noch zu tun ist (Nachtrag 3) */
  onAbgleich?: () => void
}) {
  const [lesung, setLesung] = useState<Reading | null>(null)
  const [zuordnung, setZuordnung] = useState<ReadingPairing | null>(null)
  // „Als Geschäftspartner anlegen": was der Partnerstamm als mögliche Dublette meldet, und was danach zu sagen ist
  const [aehnliche, setAehnliche] = useState<SupplierCandidate[] | null>(null)
  const [angelegt, setAngelegt] = useState<string | null>(null)
  const user = useAuthStore((s) => s.user)
  const hatPartnerstamm = useAuthStore((s) => s.hasModule)('CRM')
  const darfAnlegen = hatPartnerstamm && erfuelltRecht('crm.client.manage', user?.permissions)
  const [felder, setFelder] = useState<Set<string>>(new Set())
  const [zeilen, setZeilen] = useState<Set<number>>(new Set())
  const [laeuft, setLaeuft] = useState(false)
  const vorgewaehlt = useRef<string | null>(null)
  // AP-44 7.3: Mit der Automatik fragt „Übernehmen" die KI nach Sachkonten und wartet bis zu 20 Sekunden auf sie.
  const [autoKonten, setAutoKonten] = useState(false)
  const [fragtKonten, setFragtKonten] = useState(false)
  useEffect(() => {
    let aus = false
    fetchReadingState()
      .then((s) => !aus && setAutoKonten(s.ready && s.autoAccount))
      .catch(() => undefined)
    return () => {
      aus = true
    }
  }, [])

  const uebernimm = useCallback((l: Reading) => {
    setLesung(l)
    // Vorauswahl einmal je Lesung: alles Sichere, das noch offen ist
    const kennung = `${l.status}:${l.readAt ?? ''}`
    if (l.proposal && vorgewaehlt.current !== kennung) {
      vorgewaehlt.current = kennung
      setFelder(new Set(l.proposal.kopf.filter((f) => f.sicher && !f.entscheidung).map((f) => f.key)))
      setZeilen(new Set(l.proposal.zeilen.filter((z) => z.sicher && !z.entscheidung).map((z) => z.index)))
    }
  }, [])

  useEffect(() => {
    let aus = false
    vorgewaehlt.current = null
    setZuordnung(null)
    setAehnliche(null)
    setAngelegt(null)
    fetchReading(rechnung.id)
      .then((l) => !aus && uebernimm(l))
      .catch(() => !aus && setLesung(null))
    return () => {
      aus = true
    }
  }, [rechnung.id, uebernimm])

  // Solange gelesen wird: alle drei Sekunden nachsehen
  const wartet = lesung?.status === 'QUEUED' || lesung?.status === 'RUNNING'
  useEffect(() => {
    if (!wartet) return
    const t = setInterval(() => {
      fetchReading(rechnung.id)
        .then(uebernimm)
        .catch(() => undefined)
    }, 3000)
    return () => clearInterval(t)
  }, [wartet, rechnung.id, uebernimm])

  const tue = async (was: () => Promise<{ invoice: IncomingInvoiceDetail; reading: Reading; pairing?: ReadingPairing | null } | Reading>, text: string) => {
    try {
      setLaeuft(true)
      onFehler(null)
      const a = await was()
      if ('reading' in a) {
        onRechnung(a.invoice)
        setLesung(a.reading)
        if (a.pairing) {
          setZuordnung(a.pairing)
        }
        const offen = (k: string) => a.reading.proposal?.kopf.some((f) => f.key === k && !f.entscheidung)
        setFelder((alt) => new Set([...alt].filter(offen)))
        setZeilen((alt) => new Set([...alt].filter((i) => a.reading.proposal?.zeilen.some((z) => z.index === i && !z.entscheidung))))
      } else uebernimm(a)
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code
      onFehler((code && FEHLER[code]) ?? apiErrorText(e, text))
    } finally {
      setLaeuft(false)
    }
  }

  /** „Übernehmen": Entstehen dabei Positionen, schlägt die KI mit der Automatik gleich ihre Sachkonten vor. */
  const uebernehmen = async () => {
    setFragtKonten(autoKonten && zeilen.size > 0)
    await tue(() => applyReading(rechnung.id, { fields: [...felder], lines: [...zeilen] }), 'Der Vorschlag ließ sich nicht übernehmen.')
    setFragtKonten(false)
  }

  /** Den gelesenen Aussteller als Lieferanten anlegen — der Partnerstamm meldet eine mögliche Dublette mit 409. */
  const lieferantAnlegen = async (trotzdem: boolean) => {
    try {
      setLaeuft(true)
      onFehler(null)
      const a = await createReadingSupplier(rechnung.id, trotzdem)
      onRechnung(a.invoice)
      setLesung(a.reading)
      setAehnliche(null)
      setAngelegt(`„${a.supplier.name}" ist als Geschäftspartner angelegt und der Lieferant dieser Rechnung.${a.supplier.omitted.includes('address') ? ' Die Anschrift stand nicht vollständig auf der Rechnung — bitte am Geschäftspartner nachtragen.' : ''}`)
    } catch (e) {
      const antwort = (e as { response?: { status?: number; data?: { code?: string; message?: unknown } } }).response
      const inhalt = antwort?.data?.message
      if (antwort?.status === 409 && typeof inhalt === 'object' && inhalt !== null && 'candidates' in inhalt) setAehnliche((inhalt as { candidates: SupplierCandidate[] }).candidates)
      else onFehler((antwort?.data?.code && FEHLER[antwort.data.code]) ?? apiErrorText(e, 'Der Geschäftspartner ließ sich nicht anlegen.'))
    } finally {
      setLaeuft(false)
    }
  }

  // Fundstellen: je Feld sein Zitat (ohne Zitat ein Datum in seinen Schreibweisen), je Position ihr Betrag bei
  // ihrer Bezeichnung. Nur solange der Vorschlag offen ist — danach gilt die Rechnung, nicht mehr die Lesung.
  const fundstellen = useMemo<Fundstelle[]>(() => {
    const p = lesung?.proposal
    if (!p?.lesbar || p.offen === 0) return []
    return [
      ...p.kopf
        .filter((f) => f.entscheidung !== 'DISCARDED')
        .map((f) => ({ id: stelleKopf(f.key), seite: f.seite, texte: f.zitat ? [f.zitat] : TAGE.has(f.key) ? datumSchreibweisen(String(f.wert ?? '')) : [], titel: FELD[f.key] ?? f.key })),
      ...p.zeilen.filter((z) => z.entscheidung !== 'DISCARDED').map((z) => ({ id: stelleZeile(z.index), seite: z.seite, texte: [z.zitat], anker: z.name, titel: `Position ${z.index + 1}: ${z.name}` })),
    ].filter((f) => f.texte.length > 0 || 'anker' in f)
  }, [lesung])
  const meldeFundstellen = useRef(onFundstellen)
  meldeFundstellen.current = onFundstellen
  useEffect(() => meldeFundstellen.current?.(fundstellen), [fundstellen])

  if (!lesung) return null
  const v = lesung.proposal
  // Angeboten, solange die Rechnung keinen Lieferanten hat — danach ist die Frage erledigt.
  const neuer = v?.lesbar && !rechnung.supplierId ? v.neuerLieferant : null
  const darf = rechnung.editable
  // Ohne Lesung und ohne Recht zu bearbeiten gibt es hier nichts zu zeigen.
  if (lesung.status === 'NONE' && !darf) return null
  // Ist alles entschieden, bleibt nur die Möglichkeit, neu zu lesen — der Abschnitt tritt zurück.
  const erledigt = !!v && v.lesbar && v.offen === 0

  const lesen = (
    <KiLesenKnopf erneut={lesung.status !== 'NONE'} disabled={laeuft || wartet} onClick={() => void tue(() => startReading(rechnung.id), 'Die Lesung ließ sich nicht starten.')} />
  )
  const schalte = <T,>(menge: Set<T>, setze: (s: Set<T>) => void, wert: T) => {
    const neu = new Set(menge)
    if (neu.has(wert)) neu.delete(wert)
    else neu.add(wert)
    setze(neu)
  }
  const bisher = (f: ReadingField): string => {
    if (f.key === 'supplierId') return rechnung.supplierId && rechnung.supplierId !== f.wert ? (rechnung.supplierName ?? '') : ''
    const w = (rechnung as unknown as Record<string, unknown>)[f.key]
    const gleich = w === f.wert || (typeof w === 'number' && Number(f.wert) === w)
    return gleich || (f.key === 'kind' && w === 'INVOICE') || (f.key === 'currency' && w === 'EUR') ? '' : zeige(f.key, w)
  }
  const zelle = (z: ReadingLine, feld: string) => (z.unsicher.includes(feld) && !z.entscheidung ? kiGelb : '')
  const sichereWaehlen = () => {
    if (!v) return
    setFelder(new Set(v.kopf.filter((f) => f.sicher && !f.entscheidung).map((f) => f.key)))
    setZeilen(new Set(v.zeilen.filter((z) => z.sicher && !z.entscheidung).map((z) => z.index)))
  }

  return (
    // Eigene Farbe und gestrichelter Rand: ein Vorschlag, nicht die Rechnung (Auftraggeber 5.10.2026).
    <KiVorschlag marke={v && v.lesbar && !erledigt && 'noch nicht in der Rechnung'}>
      {lesung.status === 'NONE' && (
        <KiZustand aktion={darf && lesen}>Die KI liest Kopf und Positionen aus dem Original und schlägt sie vor. In die Rechnung kommt erst, was du übernimmst.</KiZustand>
      )}
      {wartet && <KiLiest text="Die KI liest die Rechnung">Das dauert meist unter einer Minute. Du kannst inzwischen weiterarbeiten.</KiLiest>}
      {lesung.status === 'FAILED' && (
        <KiZustand fehler aktion={darf && lesen}>
          Die Lesung ist gescheitert{lesung.error ? `: ${laufFehlerText(lesung.error)}` : '.'}
        </KiZustand>
      )}
      {neuer && (
        <div className="rounded-md border border-violet-500/40 bg-background px-3 py-2 text-sm">
          <p>
            Lieferant <strong>{neuer.name}</strong> ist noch kein Geschäftspartner.
          </p>
          <p className="mt-0.5 text-muted-foreground">
            {[
              [neuer.street, [neuer.zip, neuer.city].filter(Boolean).join(' '), neuer.country].filter(Boolean).join(', '),
              neuer.vatId && `UID ${neuer.vatId}`,
              neuer.iban && `IBAN ${neuer.iban}`,
              neuer.email,
              neuer.phone,
              neuer.website,
            ]
              .filter(Boolean)
              .join(' · ') || 'Außer dem Namen steht nichts auf der Rechnung.'}
          </p>
          {aehnliche && (
            <div className="mt-2 rounded border border-amber-500/40 bg-amber-500/5 px-2 py-1.5">
              <p className="font-medium">Diese Geschäftspartner sehen ähnlich aus — wähle einen davon im Feld „Lieferant", wenn er es ist:</p>
              <ul className="mt-1 space-y-0.5">
                {aehnliche.map((k) => (
                  <li key={k.id}>
                    <span className="font-mono text-xs text-muted-foreground">{k.supplierNumber ?? k.clientNumber}</span> {k.name}
                    {!k.isActive && <span className="ml-2 text-xs text-muted-foreground">(inaktiv)</span>}
                    {k.isActive && !k.supplierNumber && <span className="ml-2 text-xs text-muted-foreground">(bisher nur Kunde)</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {darf && darfAnlegen ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button type="button" className={kiKnopf} disabled={laeuft} onClick={() => void lieferantAnlegen(!!aehnliche)}>
                <UserPlus className="h-4 w-4" /> {aehnliche ? 'Trotzdem anlegen' : 'Als Geschäftspartner anlegen'}
              </button>
              <span className="text-xs text-muted-foreground">Legt ihn als Lieferanten an und setzt ihn an diese Rechnung — oder wähle ihn unten von Hand.</span>
            </div>
          ) : (
            darf && <p className="mt-1 text-xs text-muted-foreground">Bitte wähle den Lieferanten unten von Hand{hatPartnerstamm ? ' — zum Anlegen fehlt dir das Recht, Geschäftspartner zu verwalten' : ''}.</p>
          )}
        </div>
      )}
      {angelegt && <p className="rounded-md bg-emerald-500/10 px-3 py-1.5 text-sm">{angelegt}</p>}
      {v && !v.lesbar && <KiZustand aktion={darf && lesen}>Nicht lesbar{v.grund ? ` — ${v.grund}` : ''}. Bitte erfasse die Rechnung von Hand.</KiZustand>}
      {v && v.lesbar && erledigt && <KiZustand aktion={darf && lesen}>Der Vorschlag der KI ist abgearbeitet — alles ist übernommen oder verworfen.</KiZustand>}

      {v && v.lesbar && !erledigt && (
        <>
          <KiEinleitung hinweise={v.hinweise} zusatz={womit(lesung.context)}>
            {v.hatTextschicht ? 'Gelesen aus einem PDF mit Text.' : 'Gelesen aus einem Scan oder Foto — Rechnungsnummer und Zahlungsreferenz bitte immer am Original prüfen.'} Gelb heißt: nicht sicher gelesen, mit Grund.{' '}
            {v.hatTextschicht ? 'Im Original ist markiert, wo die Werte stehen — ein Klick auf eine Zeile springt dorthin.' : 'Ein Klick auf eine Zeile zeigt die Seite im Original.'}
            {v.offen > 0 && ` Noch ${v.offen} offen — bis dahin lässt sich die Rechnung nicht abschließen.`}
          </KiEinleitung>

          {v.kopf.length > 0 && (
            <KiTabelle
              kopf={
                <>
                  <th className="w-8 px-2 py-1.5" />
                  <th className={kiTh}>Feld</th>
                  <th className={kiTh}>gelesen</th>
                  <th className={kiTh}>steht in der Rechnung</th>
                  <th className={kiTh}>Hinweis</th>
                  <th className={kiTd} />
                </>
              }
            >
              {v.kopf.map((f) => (
                <tr
                  key={f.key}
                  data-fundstelle={stelleKopf(f.key)}
                  className={`cursor-pointer border-b border-border last:border-0 ${!f.sicher && !f.entscheidung ? kiGelb : ''} ${gewaehlt === stelleKopf(f.key) ? gewaehltKlasse : ''}`}
                  onClick={() => onZeige(stelleKopf(f.key), f.seite)}
                >
                  <KiAuswahl label={`${FELD[f.key] ?? f.key} übernehmen`} zeigen={!f.entscheidung && darf} checked={felder.has(f.key)} onChange={() => schalte(felder, setFelder, f.key)} />
                  <td className={`whitespace-nowrap ${kiTd}`}>{FELD[f.key] ?? f.key}</td>
                  <td className={`${kiTd} font-medium`}>{zeige(f.key, f.wert, f.anzeige)}</td>
                  <td className={`${kiTd} text-muted-foreground`}>{bisher(f)}</td>
                  <td className={`${kiTd} text-muted-foreground`}>{f.entscheidung ? '' : (f.grund ?? '')}</td>
                  <KiEntscheidungZelle entscheidung={f.entscheidung} darf={darf} disabled={laeuft} onVerwerfen={() => void tue(() => discardReading(rechnung.id, { fields: [f.key] }), 'Das Feld ließ sich nicht verwerfen.')} />
                </tr>
              ))}
            </KiTabelle>
          )}

          {v.zeilen.length > 0 && (
            <KiTabelle
              kopf={
                <>
                  <th className="w-8 px-2 py-1.5" />
                  <th className={kiTh}>Bezeichnung</th>
                  <th className={`${kiTh} text-right`}>Menge</th>
                  <th className={kiTh}>Einheit</th>
                  <th className={`${kiTh} text-right`}>Einzelpreis</th>
                  <th className={`${kiTh} text-right`}>PE</th>
                  <th className={`${kiTh} text-right`}>Rabatt %</th>
                  <th className={`${kiTh} text-right`}>Netto</th>
                  <th className={`${kiTh} text-right`}>Steuer %</th>
                  <th className={kiTh}>Hinweis</th>
                  <th className={kiTd} />
                </>
              }
            >
              {v.zeilen.map((z) => (
                <tr
                  key={z.index}
                  data-fundstelle={stelleZeile(z.index)}
                  className={`cursor-pointer border-b border-border last:border-0 ${gewaehlt === stelleZeile(z.index) ? gewaehltKlasse : ''}`}
                  onClick={() => onZeige(stelleZeile(z.index), z.seite)}
                >
                  <KiAuswahl label={`Position ${z.index + 1} übernehmen`} zeigen={!z.entscheidung && darf} checked={zeilen.has(z.index)} onChange={() => schalte(zeilen, setZeilen, z.index)} />
                  <td className={`${kiTd} ${zelle(z, 'name')}`}>
                    {z.name}
                    {z.supplierArticleNumber && <span className="ml-2 text-xs text-muted-foreground">{z.supplierArticleNumber}</span>}
                  </td>
                  <td className={`${kiTd} text-right tabular-nums ${zelle(z, 'quantity')}`}>{z.quantity !== null ? mengeText(z.quantity) : ''}</td>
                  <td className={`${kiTd} ${zelle(z, 'unitCode')}`}>{z.unitCode ?? ''}</td>
                  <td className={`${kiTd} text-right tabular-nums ${zelle(z, 'unitPrice')}`}>{z.unitPrice !== null ? geldText(z.unitPrice) : ''}</td>
                  <td className={`${kiTd} text-right tabular-nums ${zelle(z, 'priceUnit')}`}>{z.priceUnit !== 1 ? z.priceUnit : ''}</td>
                  <td className={`${kiTd} text-right tabular-nums ${zelle(z, 'discountPercent')}`}>{z.discountPercent ? mengeText(z.discountPercent) : ''}</td>
                  <td className={`${kiTd} text-right tabular-nums ${zelle(z, 'lineNet')}`}>{z.lineNet !== null ? geldText(z.lineNet) : ''}</td>
                  <td className={`${kiTd} text-right tabular-nums ${zelle(z, 'taxRate')}`}>{z.taxRate !== null ? mengeText(z.taxRate) : ''}</td>
                  <td className={`${kiTd} text-muted-foreground`}>{z.entscheidung ? '' : z.gruende.join(' · ')}</td>
                  <KiEntscheidungZelle entscheidung={z.entscheidung} darf={darf} disabled={laeuft} onVerwerfen={() => void tue(() => discardReading(rechnung.id, { lines: [z.index] }), 'Die Zeile ließ sich nicht verwerfen.')} />
                </tr>
              ))}
            </KiTabelle>
          )}

          {darf && (
            <KiFuss
              anzahl={felder.size + zeilen.size}
              laeuft={laeuft}
              onUebernehmen={() => void uebernehmen()}
              onSichere={sichereWaehlen}
              onRestVerwerfen={() => void tue(() => discardReading(rechnung.id, { all: true }), 'Der Rest ließ sich nicht verwerfen.')}
              nebenbei={fragtKonten && <KiArbeitet text="Die KI schlägt Sachkonten vor" className={`text-sm ${kiFarbe}`} />}
              lesen={lesen}
            />
          )}
        </>
      )}
      {zuordnung && (
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-muted/50 px-3 py-1.5 text-sm">
          <span>{zuordnungText(zuordnung)}</span>
          {onAbgleich && (
            <button type="button" className="underline hover:text-foreground" onClick={onAbgleich}>
              Abgleich öffnen
            </button>
          )}
        </p>
      )}
    </KiVorschlag>
  )
}
